import { computed, ref } from 'vue';
import type { RoutePlan, RoutePlanVisit, RouteAssignment } from '@/api/route-plans';

// The board's dialog and picker state: which visit/shift editor is open, the empty-slot
// picker, and the create-dialog prefill. Extracted from route-plan-board.vue so the board
// only orchestrates.

const TIME_PAD = 2;

interface DialogOptions {
  routePlan: () => RoutePlan;
  pendingVisits: () => RoutePlanVisit[];
}

// eslint-disable-next-line max-statements
export function useRoutePlanBoardDialogs({ routePlan, pendingVisits }: DialogOptions) {
  const dialogOpen = ref(false);
  const dialogVisitId = ref<number | null>(null);
  const prefillCaregiverId = ref<number | null>(null);
  const prefillArrivalTime = ref<string | null>(null);

  const createDialogOpen = ref(false);
  // Set when the create dialog was opened from an empty slot: it seeds "hora fija" at the
  // clicked time and the visit is placed on that caregiver directly on save.
  const createPrefill = ref<{ caregiverId: number; arrivalTime: string } | null>(null);

  const addCaregiverOpen = ref(false);
  const editCaregiverOpen = ref(false);
  const editCaregiverRpcId = ref<number | null>(null);
  const shiftDialogOpen = ref(false);
  const shiftDialogId = ref<number | null>(null);

  const pickerOpen = ref(false);
  const pickerCaregiverId = ref<number | null>(null);
  const pickerArrival = ref<string>('');

  const dialogVisit = computed(
    () => routePlan().routePlanVisits.find(v => v.id === dialogVisitId.value) ?? null,
  );
  const dialogAssignment = computed(
    () => routePlan().routeAssignments.find(a => a.routePlanVisitId === dialogVisitId.value) ??
      null,
  );

  const assignmentById = computed<Record<number, RouteAssignment>>(() => Object.fromEntries(
    routePlan().routeAssignments.map((assignment) => [assignment.id, assignment]),
  ));

  function openVisit(
    visitId: number, prefill?: { caregiverId: number | null; arrivalTime: string },
  ) {
    prefillCaregiverId.value = prefill?.caregiverId ?? null;
    prefillArrivalTime.value = prefill?.arrivalTime ?? null;
    dialogVisitId.value = visitId;
    dialogOpen.value = true;
  }

  function onShiftClick(shiftId: number) {
    shiftDialogId.value = shiftId;
    shiftDialogOpen.value = true;
  }

  function openCreateDialog(prefill: { caregiverId: number; arrivalTime: string } | null = null) {
    createPrefill.value = prefill;
    createDialogOpen.value = true;
  }

  // Calendar column-heading click → edit that caregiver's working hours in place.
  function openCaregiverEdit(caregiverId: number) {
    const routePlanCaregiver = routePlan().routePlanCaregivers
      .find(rpc => rpc.caregiverId === caregiverId);
    if (!routePlanCaregiver) return;

    editCaregiverRpcId.value = routePlanCaregiver.id;
    editCaregiverOpen.value = true;
  }

  // Calendar block click → open the editor for that assignment's visit (existing shifts are
  // routed to onShiftClick by the calendar itself).
  function onEventClick(assignmentId?: number) {
    const assignment = assignmentId ? assignmentById.value[assignmentId] : undefined;
    if (assignment) openVisit(assignment.routePlanVisitId);
  }

  // Clicked cell → the START of the clicked hour ("12:37" → "12:00"): the natural suggestion
  // when picking a slot by eye; fine-tuning happens in the dialog or by dragging.
  function cellArrival(date: Date): string {
    return `${String(date.getHours()).padStart(TIME_PAD, '0')}:00`;
  }

  // Empty-slot click → pick which pending visit to drop here; with nothing pending, jump
  // straight to creating a fixed-time visit on this caregiver at the clicked time.
  function onCellClick({ date, scheduleId }: { date: Date; scheduleId: number | null }) {
    if (scheduleId === null) return;

    if (!pendingVisits().length) {
      openCreateDialog({ caregiverId: scheduleId, arrivalTime: cellArrival(date) });

      return;
    }

    pickerCaregiverId.value = scheduleId;
    pickerArrival.value = cellArrival(date);
    pickerOpen.value = true;
  }

  function pickVisit(visit: RoutePlanVisit) {
    pickerOpen.value = false;
    openVisit(visit.id, { caregiverId: pickerCaregiverId.value, arrivalTime: pickerArrival.value });
  }

  function caregiverName(caregiverId: number | null): string {
    return routePlan().routePlanCaregivers
      .find(c => c.caregiverId === caregiverId)?.caregiverFullName ?? '';
  }

  return {
    dialogOpen,
    dialogVisitId,
    dialogVisit,
    dialogAssignment,
    prefillCaregiverId,
    prefillArrivalTime,
    createDialogOpen,
    createPrefill,
    addCaregiverOpen,
    editCaregiverOpen,
    editCaregiverRpcId,
    shiftDialogOpen,
    shiftDialogId,
    pickerOpen,
    pickerCaregiverId,
    pickerArrival,
    openVisit,
    onShiftClick,
    openCreateDialog,
    openCaregiverEdit,
    onEventClick,
    onCellClick,
    pickVisit,
    caregiverName,
  };
}
